import {
  Activity,
  BadgeCheck,
  BookOpenCheck,
  BriefcaseBusiness,
  Check,
  ChevronRight,
  Compass,
  Dumbbell,
  Heart,
  House,
  Pencil,
  Plus,
  Search,
  Sparkles,
  Trash2,
  X
} from "lucide-react";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Button, EmptyState, PageHeader } from "../components/ui";
import { useApp } from "../context/AppContext";
import { api } from "../lib/api";
import { formatDate } from "../lib/format";
import type { Skill, SkillEvidence, Task } from "../types";

type CatalogueView = "WORK" | "LIFE" | "INTEREST";
type GrowthId = Skill["growth_dimension_id"];

interface SkillIdea {
  title: string;
  category: string;
  growthDimensionId: GrowthId;
}

const workIdeas: SkillIdea[] = [
  { title: "客户开发", category: "外贸", growthDimensionId: "attr-career" },
  { title: "商务谈判", category: "沟通", growthDimensionId: "attr-career" },
  { title: "服装报价", category: "外贸", growthDimensionId: "attr-career" },
  { title: "面辅料判断", category: "服装", growthDimensionId: "attr-career" },
  { title: "订单跟进", category: "外贸", growthDimensionId: "attr-career" },
  { title: "商务英语", category: "语言", growthDimensionId: "attr-career" },
  { title: "项目管理", category: "管理", growthDimensionId: "attr-career" },
  { title: "数据分析", category: "数字", growthDimensionId: "attr-career" },
  { title: "AI 工具", category: "数字", growthDimensionId: "attr-career" },
  { title: "内容创作", category: "表达", growthDimensionId: "attr-career" }
];

const lifeIdeas: SkillIdea[] = [
  { title: "游泳", category: "运动", growthDimensionId: "attr-physique" },
  { title: "跑步", category: "运动", growthDimensionId: "attr-physique" },
  { title: "力量训练", category: "运动", growthDimensionId: "attr-physique" },
  { title: "烹饪", category: "生活", growthDimensionId: "attr-experience" },
  { title: "驾驶", category: "生活", growthDimensionId: "attr-experience" },
  { title: "家庭整理", category: "生活", growthDimensionId: "attr-experience" },
  { title: "基础维修", category: "生活", growthDimensionId: "attr-experience" },
  { title: "应急处理", category: "生活", growthDimensionId: "attr-experience" }
];

const interestIdeas: SkillIdea[] = [
  { title: "摄影", category: "审美", growthDimensionId: "attr-experience" },
  { title: "阅读", category: "阅读", growthDimensionId: "attr-experience" },
  { title: "音乐", category: "艺术", growthDimensionId: "attr-experience" },
  { title: "书法", category: "艺术", growthDimensionId: "attr-experience" },
  { title: "绘画", category: "艺术", growthDimensionId: "attr-experience" },
  { title: "园艺", category: "生活美学", growthDimensionId: "attr-experience" },
  { title: "旅行", category: "探索", growthDimensionId: "attr-experience" },
  { title: "茶艺", category: "生活美学", growthDimensionId: "attr-experience" }
];

const growthMeta: Record<GrowthId, { label: string; description: string; icon: typeof Activity }> = {
  "attr-career": { label: "事业", description: "每条实践为事业增加少量长期积累", icon: BriefcaseBusiness },
  "attr-experience": { label: "经历", description: "每条实践沉淀为经历与生活能力", icon: Compass },
  "attr-physique": { label: "体魄", description: "每条运动实践进入体魄长期积累", icon: Dumbbell }
};

const viewMeta: Record<CatalogueView, { label: string; description: string; icon: typeof Activity }> = {
  WORK: { label: "工作技能", description: "服装外贸与职业能力", icon: BriefcaseBusiness },
  LIFE: { label: "生活技能", description: "运动与独立生活能力", icon: House },
  INTEREST: { label: "兴趣", description: "爱好与陶冶情操", icon: Heart }
};

export function LearningPage() {
  const { data, openModal, mutate } = useApp();
  const [view, setView] = useState<CatalogueView>("WORK");
  const [query, setQuery] = useState("");
  const [selectedSkillId, setSelectedSkillId] = useState<string | null>(null);
  if (!data) return null;

  const workSkills = data.skills.filter((item) => item.kind === "SKILL" && item.sphere === "WORK");
  const lifeSkills = data.skills.filter((item) => item.kind === "SKILL" && item.sphere === "LIFE");
  const interests = data.skills.filter((item) => item.kind === "INTEREST");
  const source = view === "WORK" ? workSkills : view === "LIFE" ? lifeSkills : interests;
  const normalizedQuery = query.trim().toLowerCase();
  const visibleSkills = source.filter((item) =>
    !normalizedQuery || `${item.title} ${item.category} ${item.description}`.toLowerCase().includes(normalizedQuery)
  );
  const selectedSkill = data.skills.find((item) => item.id === selectedSkillId) ?? null;
  const ideas = view === "WORK" ? workIdeas : view === "LIFE" ? lifeIdeas : interestIdeas;
  const totalEvidence = data.skills.reduce((sum, item) => sum + Number(item.evidence_count), 0);
  const activePractice = data.skills.filter((item) => Number(item.evidence_count) > 0).length;

  const addIdea = (idea: SkillIdea) =>
    mutate(
      () => api.create("skills", {
        title: idea.title,
        description: "",
        kind: view === "INTEREST" ? "INTEREST" : "SKILL",
        sphere: view === "WORK" ? "WORK" : "LIFE",
        category: idea.category,
        growthDimensionId: idea.growthDimensionId
      }),
      `已添加：${idea.title}`
    );

  return (
    <div className="page page--learning skills-page">
      <PageHeader
        eyebrow="SKILLS & INTERESTS · 长期熟练度"
        title="技能与兴趣"
        description="你反复练习的事，终会成为无需思考的能力。"
        actions={<Button onClick={() => openModal("skill")} icon={<Plus size={16} />}>添加技能</Button>}
      />

      <section className="skills-pulse" aria-label="技能成长概况">
        <div><strong>{data.skills.length}</strong><span>已收录</span></div>
        <div><strong>{activePractice}</strong><span>已有实践</span></div>
        <div><strong>{totalEvidence}</strong><span>事实证据</span></div>
        <p><Sparkles size={16} /> 完成“游泳、跑步、力量训练”等事项后，系统会自动建立或更新相应技能。</p>
      </section>

      <section className="skills-catalogue" aria-labelledby="skills-catalogue-title">
        <header className="skills-catalogue__header">
          <div>
            <h2 id="skills-catalogue-title">我的技能目录</h2>
            <p>{viewMeta[view].description} · 点开任意技能查看等级、证据和关联任务。</p>
          </div>
          <label className="skills-search">
            <Search size={16} aria-hidden="true" />
            <span className="sr-only">搜索当前技能</span>
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索技能" />
            {query && <button type="button" onClick={() => setQuery("")} aria-label="清空搜索"><X size={15} /></button>}
          </label>
        </header>

        <nav className="skills-view-tabs" aria-label="技能分类">
          {(["WORK", "LIFE", "INTEREST"] as CatalogueView[]).map((item) => {
            const meta = viewMeta[item];
            const Icon = meta.icon;
            const count = item === "WORK" ? workSkills.length : item === "LIFE" ? lifeSkills.length : interests.length;
            return (
              <button
                key={item}
                type="button"
                className={view === item ? "is-active" : ""}
                aria-pressed={view === item}
                onClick={() => {
                  setView(item);
                  setQuery("");
                }}
              >
                <Icon size={16} />
                <span>{meta.label}</span>
                <b>{count}</b>
              </button>
            );
          })}
        </nav>

        {visibleSkills.length ? (
          <div className="skills-grid">
            {visibleSkills.map((item) => (
              <SkillTile key={item.id} skill={item} onOpen={() => setSelectedSkillId(item.id)} />
            ))}
          </div>
        ) : normalizedQuery ? (
          <EmptyState
            icon={<Search size={22} />}
            title="没有匹配的技能"
            description="换一个关键词，或添加新的技能。"
            actionLabel="清空搜索"
            onAction={() => setQuery("")}
            compact
          />
        ) : (
          <EmptyState
            icon={view === "INTEREST" ? <Heart size={22} /> : <Sparkles size={22} />}
            title={`${viewMeta[view].label}还是空的`}
            description="从下面的候选开始，或添加你自己的技能。"
            actionLabel="添加技能"
            onAction={() => openModal("skill")}
            compact
          />
        )}
      </section>

      <section className="skills-suggestions" aria-labelledby="skills-suggestions-title">
        <div>
          <h2 id="skills-suggestions-title">添加候选</h2>
          <p>候选不会自动进入档案；点击后才会创建。真实运动记录例外，它会在确认后自动识别。</p>
        </div>
        <div className="skills-suggestion-list">
          {ideas.map((idea) => {
            const exists = data.skills.some((item) => item.title === idea.title);
            return (
              <button key={idea.title} type="button" disabled={exists} onClick={() => void addIdea(idea)}>
                {exists ? <Check size={14} /> : <Plus size={14} />}
                <span>{idea.title}</span>
              </button>
            );
          })}
        </div>
      </section>

      {selectedSkill && (
        <SkillDetailDialog
          skill={selectedSkill}
          evidence={(data.skillEvidence ?? []).filter((item) => item.skill_id === selectedSkill.id)}
          tasks={data.tasks.filter((item) => item.skill_id === selectedSkill.id)}
          onClose={() => setSelectedSkillId(null)}
        />
      )}
    </div>
  );
}

function SkillTile({ skill, onOpen }: { skill: Skill; onOpen: () => void }) {
  const growth = growthMeta[skill.growth_dimension_id];
  const GrowthIcon = growth.icon;
  const level = Number(skill.level ?? 0);
  const levelProgress = Number(skill.level_progress ?? 0);
  const style = { "--skill-progress": levelProgress / 100 } as CSSProperties;
  return (
    <button className="skill-tile" type="button" onClick={onOpen} style={style}>
      <span className="skill-tile__icon">
        {skill.kind === "INTEREST" ? <Heart size={17} /> : skill.growth_dimension_id === "attr-physique" ? <Dumbbell size={17} /> : <Sparkles size={17} />}
      </span>
      <span className="skill-tile__identity">
        <small>{skill.category || "未分类"}{skill.auto_created ? " · 自动识别" : ""}</small>
        <strong>{skill.title}</strong>
      </span>
      <span className="skill-tile__level">Lv.{level}</span>
      <span className="skill-tile__progress" aria-label={`当前等级进度 ${levelProgress}%`}><i /></span>
      <span className="skill-tile__meta"><GrowthIcon size={13} /> {growth.label} · {skill.evidence_count} 次实践</span>
      <ChevronRight className="skill-tile__arrow" size={16} />
    </button>
  );
}

function SkillDetailDialog({
  skill,
  evidence,
  tasks,
  onClose
}: {
  skill: Skill;
  evidence: SkillEvidence[];
  tasks: Task[];
  onClose: () => void;
}) {
  const { mutate } = useApp();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(skill.title);
  const [description, setDescription] = useState(skill.description);
  const [category, setCategory] = useState(skill.category);
  const [kind, setKind] = useState<Skill["kind"]>(skill.kind);
  const [sphere, setSphere] = useState<Skill["sphere"]>(skill.sphere);
  const [growthDimensionId, setGrowthDimensionId] = useState<GrowthId>(skill.growth_dimension_id);
  const growth = growthMeta[skill.growth_dimension_id];
  const GrowthIcon = growth.icon;
  const level = Number(skill.level ?? 0);
  const levelProgress = Number(skill.level_progress ?? 0);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) dialog.showModal();
  }, []);

  const save = async () => {
    const result = await mutate(
      () => api.update("skills", skill.id, { title, description, category, kind, sphere, growthDimensionId }),
      "技能已更新"
    );
    if (result) setEditing(false);
  };

  const remove = async () => {
    const result = await mutate(() => api.remove("skills", skill.id), "已移入回收站，可在设置中恢复");
    if (result) onClose();
  };

  return (
    <dialog
      ref={dialogRef}
      className="skill-dialog"
      aria-labelledby="skill-dialog-title"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="skill-dialog__surface">
        <header className="skill-dialog__header">
          <div>
            <span>{skill.auto_created ? <BadgeCheck size={15} /> : <BookOpenCheck size={15} />}{skill.auto_created ? "由事实自动识别" : "个人技能档案"}</span>
            <h2 id="skill-dialog-title">{skill.title}</h2>
            <p>{skill.category} · {skill.sphere === "WORK" ? "工作技能" : skill.kind === "INTEREST" ? "兴趣" : "生活技能"}</p>
          </div>
          <button type="button" className="skill-dialog__close" onClick={onClose} aria-label="关闭技能详情"><X size={19} /></button>
        </header>

        {editing ? (
          <div className="skill-editor">
            <label><span>名称</span><input value={title} onChange={(event) => setTitle(event.target.value)} autoFocus /></label>
            <div className="skill-editor__row">
              <label><span>类型</span><select value={kind} onChange={(event) => setKind(event.target.value as Skill["kind"])}><option value="SKILL">技能</option><option value="INTEREST">兴趣</option></select></label>
              <label><span>场景</span><select value={sphere} onChange={(event) => setSphere(event.target.value as Skill["sphere"])}><option value="WORK">工作</option><option value="LIFE">生活</option></select></label>
            </div>
            <div className="skill-editor__row">
              <label><span>分类</span><input value={category} onChange={(event) => setCategory(event.target.value)} /></label>
              <label><span>主要影响</span><select value={growthDimensionId} onChange={(event) => setGrowthDimensionId(event.target.value as GrowthId)}><option value="attr-career">事业</option><option value="attr-experience">经历</option><option value="attr-physique">体魄</option></select></label>
            </div>
            <label><span>说明</span><textarea value={description} onChange={(event) => setDescription(event.target.value)} rows={4} /></label>
            <footer><Button variant="ghost" onClick={() => setEditing(false)}>取消</Button><Button onClick={() => void save()} icon={<Check size={15} />}>保存技能</Button></footer>
          </div>
        ) : (
          <>
            <section className="skill-level-card" aria-label="技能等级">
              <div><strong>Lv.{level}</strong><span>{skill.level_title}</span></div>
              <div className="skill-level-card__track"><i style={{ transform: `scaleX(${levelProgress / 100})` }} /></div>
              <p><b>{skill.total_xp} XP</b><span>距离 Lv.{level + 1} 还需 {skill.xp_to_next_level} XP</span></p>
            </section>

            <section className="skill-dialog__section">
              <h3>这项技能是什么</h3>
              <p>{skill.description || "尚未填写说明。你可以补充它能解决什么问题，以及怎样算真正掌握。"}</p>
            </section>

            <section className="skill-impact">
              <GrowthIcon size={20} />
              <div><strong>关联{growth.label}</strong><p>{growth.description}；目前已有 {skill.evidence_count} 条有效贡献。</p></div>
            </section>

            <section className="skill-dialog__section">
              <div className="skill-dialog__section-head"><h3>最近实践证据</h3><span>{evidence.length} 条</span></div>
              {evidence.length ? (
                <div className="skill-evidence-list">
                  {evidence.slice(0, 8).map((item) => (
                    <article key={item.id}>
                      <span><Activity size={15} /></span>
                      <div><strong>{item.event_title}</strong><small>{formatDate(item.occurred_at)} · +{item.xp_delta} XP</small><p>{item.reason}</p></div>
                    </article>
                  ))}
                </div>
              ) : <p className="skill-dialog__empty">还没有实践证据。完成关联任务，或确认一条相关事实后才会增长。</p>}
            </section>

            <section className="skill-dialog__section">
              <div className="skill-dialog__section-head"><h3>关联任务</h3><span>{tasks.length} 个</span></div>
              {tasks.length ? (
                <div className="skill-task-list">
                  {tasks.slice(0, 6).map((task) => (
                    <div key={task.id}>
                      <span className={task.status === "COMPLETED" ? "is-done" : ""}>{task.status === "COMPLETED" ? <Check size={13} /> : null}</span>
                      <strong>{task.title}</strong>
                      <small>{task.status === "COMPLETED" ? "已完成" : "待实践"}</small>
                    </div>
                  ))}
                </div>
              ) : <p className="skill-dialog__empty">暂未关联任务。创建任务时选择这项技能，完成后会自动产生证据。</p>}
            </section>
          </>
        )}

        {!editing && <footer className="skill-dialog__actions"><Button variant="ghost" onClick={() => void remove()} icon={<Trash2 size={15} />}>移入回收站</Button><Button variant="secondary" onClick={() => setEditing(true)} icon={<Pencil size={15} />}>编辑技能</Button></footer>}
      </div>
    </dialog>
  );
}
